import { useMemo, useState } from 'react';
import { advancedThreadForItem, isAiJobActive, lastAnsweredJobOfThread, type V2AiJob } from '../../domain/aiJob';
import type { V2Area, V2ChecklistItem, V2Inspection } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { aiFailureMessage } from '../../i18n/labels';
import { aiJobsStore, askAdvancedFollowUpAction } from '../../state/aiJobsStore';
import { aiPolicyBlockFor } from '../../state/aiStore';
import { useStore } from '../../state/createStore';
import { inspectionStore, setItemNoteAction } from '../../state/inspectionStore';
import { Button, InlineMessage, SheetShell, TextArea, showToast } from '../../ui/components';
import { useOnlineStatus } from '../../ui/hooks';

// 高级 AI 对话 (Final Owner Decision 2026-09-14, CA-10 / §12): the conversation about THIS row only —
// the deep review first, then every question and answer. The context (row, guidance, linked photos,
// AI observations, the review, the inspector note, the history) travels with every question; the
// inspector never re-explains. The conversation is LAYER 1: nothing in it changes the note, the result
// or the report until the inspector taps 加入验屋师备注 and saves what they chose (§12.4 / §12.5).
export function AdvancedChatSheet(props: { inspection: V2Inspection; area: V2Area; item: V2ChecklistItem; locked: boolean; onClose: () => void; onAdjustPhotos: () => void }) {
  const { t } = useT();
  const online = useOnlineStatus();
  const jobs = useStore(aiJobsStore, (s) => s.jobs);
  const liveItem = useStore(inspectionStore, (s) => s.items.find((i) => i.id === props.item.id) ?? props.item);
  const thread = useMemo(() => advancedThreadForItem(jobs, props.item.id), [jobs, props.item.id]);
  const last = thread[thread.length - 1]?.job ?? null;
  const answering = last !== null && isAiJobActive(last);
  // PP-21 layer 1: a question is a cloud call — offered only where the policy allows one.
  const canAsk = !props.locked && online && aiPolicyBlockFor(props.inspection) === null && lastAnsweredJobOfThread(thread) !== null && !answering;
  const [question, setQuestion] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function send() {
    const text = question.trim();
    if (text.length === 0 || busy) return;
    setBusy(true);
    setError(null);
    try {
      const outcome = await askAdvancedFollowUpAction(props.item.id, text);
      if (outcome.ok) setQuestion('');
      else setError(t('chat.failed', { reason: outcome.code === 'offline' ? t('ai.needsOnline') : aiFailureMessage(t, outcome.code) }));
    } finally {
      setBusy(false);
    }
  }

  // §12.5: the inspector decides what enters the note, and edits it first when they want to.
  async function addToNote() {
    const text = (noteDraft ?? '').trim();
    setNoteDraft(null);
    if (text.length === 0) return;
    const existing = liveItem.inspectorNote.trim();
    await setItemNoteAction(props.item.id, existing.length > 0 ? `${existing}\n${text}` : text);
    showToast(t('chat.addedToast'));
  }

  function answerText(job: V2AiJob): string {
    const r = job.result;
    if (!r) return '';
    const parts = [r.summary];
    if (r.attention.length > 0) parts.push(r.attention.join(' '));
    if (r.question) parts.push(r.question);
    return parts.filter((p) => p.trim().length > 0).join('\n');
  }

  return (
    <SheetShell
      full
      title={t('chat.title')}
      subtitle={`${t('chat.subtitle')} · ${liveItem.title}`}
      onClose={props.onClose}
      layer="over"
      testId="advanced-chat-sheet"
      footer={
        !props.locked ? (
          <>
            <TextArea value={question} onChange={setQuestion} placeholder={t('chat.placeholder')} rows={2} disabled={!canAsk} testId="advanced-chat-input" />
            <Button icon="psychology" onClick={() => void send()} disabled={!canAsk || question.trim().length === 0} testId="advanced-chat-send">
              {t('chat.send')}
            </Button>
          </>
        ) : undefined
      }
    >
      <p className="card-note" data-testid="advanced-chat-usage">
        {t('chat.usageHint')}
      </p>
      <div className="chat-thread" data-testid="advanced-chat-thread">
        {thread.map((turn) => (
          <div key={turn.job.id} className="chat-turn" data-testid="advanced-chat-turn" data-job-id={turn.job.id}>
            {turn.question !== null ? (
              <div className="chat-bubble chat-bubble-you" data-testid="advanced-chat-question">
                <span className="chat-who">{t('chat.you')}</span>
                <p className="card-body">{turn.question}</p>
              </div>
            ) : null}
            <div className="chat-bubble chat-bubble-ai" data-testid="advanced-chat-answer" data-status={turn.job.status}>
              <span className="chat-who">{t('chat.ai')}</span>
              {turn.job.status === 'completed' && turn.job.result ? (
                <>
                  <p className="card-body">{answerText(turn.job)}</p>
                  {!props.locked ? (
                    <Button variant="text" size="small" block={false} icon="edit_note" onClick={() => setNoteDraft(answerText(turn.job))} testId="advanced-chat-add-note">
                      {t('chat.addToNote')}
                    </Button>
                  ) : null}
                </>
              ) : isAiJobActive(turn.job) ? (
                <p className="card-note">{t('chat.answering')}</p>
              ) : (
                <p className="card-note">{turn.job.errorCode ? aiFailureMessage(t, turn.job.errorCode) : t('advanced.status.failedFinal')}</p>
              )}
            </div>
          </div>
        ))}
      </div>
      {!online ? <InlineMessage kind="info" text={t('ai.needsOnline')} /> : null}
      {error ? <InlineMessage kind="err" text={error} /> : null}
      <Button variant="text" size="small" block={false} icon="perm_media" onClick={() => { props.onClose(); props.onAdjustPhotos(); }} testId="advanced-chat-adjust-photos">
        {t('advanced.adjustPhotos')}
      </Button>
      <p className="card-note" data-testid="advanced-chat-not-record">
        {t('chat.notRecord')}
      </p>
      <p className="ai-disclaimer">{t('ai.disclaimer')}</p>
      {noteDraft !== null ? (
        <SheetShell
          title={t('chat.addToNote')}
          onClose={() => setNoteDraft(null)}
          layer="top"
          testId="advanced-chat-note-sheet"
          footer={
            <>
              <Button onClick={() => void addToNote()} disabled={noteDraft.trim().length === 0} testId="advanced-chat-note-save">
                {t('common.save')}
              </Button>
              <Button variant="text" onClick={() => setNoteDraft(null)}>
                {t('shell.cancel')}
              </Button>
            </>
          }
        >
          <p className="card-note">{t('chat.addToNoteHint')}</p>
          <TextArea value={noteDraft} onChange={setNoteDraft} rows={5} testId="advanced-chat-note-text" />
        </SheetShell>
      ) : null}
    </SheetShell>
  );
}
